import axios from "axios";

const apiUrl = process.env.REACT_APP_API_URL;

// Temporary storage for username/password
let tempUserCredentials = { username: "", password: "" };

/**
 * CRITICAL FIX: Migrate dealer data from localStorage to sessionStorage
 * This ensures sensitive PII is not persisted across sessions
 * Called after storing new dealer data to migrate any existing localStorage data
 */
const migrateDealerDataToSessionStorage = () => {
  try {
    // List of dealer-related keys to migrate
    const dealerKeys = [
      'dealerInfo',
      'dealerCode',
      'empName',
      'forCode',
      'outletCode',
      'locationCode',
      'multiServiceDealerCode',
      'dealerGstStateCode'
    ];
    
    dealerKeys.forEach(key => {
      const value = localStorage.getItem(key);
      if (value) {
        // Only migrate if not already in sessionStorage
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, value);
          console.log(`Migrated ${key} from localStorage to sessionStorage`);
        }
        // Remove from localStorage after migration
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.error("Error migrating dealer data to sessionStorage:", error);
  }
};

// -------------------------
// MASS Login: send OTP
// -------------------------
const login = async (username, password) => {
  tempUserCredentials = { username, password }; // store temporarily

  const API_URL = `${apiUrl}/api/v1/authentication/sendOtp`;

  try {
    const response = await axios.post(
      API_URL,
      { userName: username, password, userType: "D" },
      { withCredentials: true }
    );

    console.log("OTP sent response:", response.data);
    return response.data;
  } catch (error) {
    console.error("MASS Authentication failed:", error);
    
    // Check if error response has statusMessage (onboarding status error, already logged in, etc.)
    if (error.response?.data?.statusMessage) {
      throw new Error(error.response.data.statusMessage);
    }
    
    // Check if error response has message field
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    
    // Default error message
    throw new Error("Invalid Credentials");
  }
};

// -------------------------
// MSIL Login: OAuth simulation
// -------------------------
const loginMSIL = async () => {
  return new Promise((resolve) => {
    setTimeout(() => {
      console.log("Simulating MSIL OAuth2 login success.");
      resolve({ token: "msil-oauth-dummy-token", role: "MSIL" });
    }, 1000);
  });
};

// -------------------------
// Verify OTP and call /validate
// -------------------------
const verifyOtp = async (payload) => {
  const API_URL_VERIFY_OTP = `${apiUrl}/api/v1/authentication/verify-otp`;

  try {
    // 1️⃣ Verify OTP
    const otpResponse = await axios.post(API_URL_VERIFY_OTP, payload, {
      withCredentials: true,
      headers: { "Content-Type": "application/json" },
    });

    console.log("OTP verification successful:", otpResponse.data);

    const token = otpResponse.data?.token;
    if (!token) throw new Error("OTP verification failed: no token returned");

    // 2️⃣ Call /validate using temp credentials
    const API_VALIDATE = `${apiUrl}/api/v1/dealerShipDetails/validate`;
    const validateResponse = await axios.post(
      API_VALIDATE,
      {
        userName: tempUserCredentials.username,
        password: tempUserCredentials.password,
        userType: "D",
      },
      {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true, // ensure cookies are sent
      }
    );

    const dealerData = validateResponse?.data;

    if (!dealerData || Object.keys(dealerData).length === 0) {
      console.error("Dealer data is empty:", dealerData);
      throw new Error("Dealer validation returned empty data");
    }

    // CRITICAL FIX: Store dealer data in sessionStorage instead of localStorage
    // sessionStorage clears when tab closes, reducing risk of sensitive PII exposure
    // Sensitive data (GST, PAN, CEO email) should not persist across sessions
    try {
      sessionStorage.setItem("dealerInfo", JSON.stringify(dealerData));
      console.log("Dealer info stored in sessionStorage after OTP verification");
      // Migrate any existing localStorage data to sessionStorage
      migrateDealerDataToSessionStorage();
    } catch (storageError) {
      console.error("Failed to save dealer info to sessionStorage:", storageError);
      throw new Error("Could not store dealer info locally");
    }

    // ✅ Return both token and dealer info
    return { token, dealerData };
  } catch (error) {
    console.error(
      "OTP verification or dealer validation failed:",
      error.response?.data || error.message
    );
    throw new Error("OTP verification or dealer validation failed");
  }
};


// -------------------------
// Sign up / direct validate
// -------------------------
const signUp = async (username, password) => {
  const API_URL = `${apiUrl}/api/v1/dealerShipDetails/validate`;

  try {
    const response = await axios.post(API_URL, {
      userName: username,
      password,
      userType: "D",
    });

    const dealerData = response.data;
    // CRITICAL FIX: Store dealer data in sessionStorage instead of localStorage
    // sessionStorage clears when tab closes, reducing risk of sensitive PII exposure
    sessionStorage.setItem("dealerInfo", JSON.stringify(dealerData));
    console.log("DealerShip Validation Response stored in sessionStorage");
    // Migrate any existing localStorage data to sessionStorage
    migrateDealerDataToSessionStorage();

    return dealerData;
  } catch (error) {
    console.error("MASS Validation failed:", error);
    throw new Error("Invalid Credentials");
  }
};

const AuthService = {
  login,
  loginMSIL,
  verifyOtp,
  signUp,
};

export default AuthService;
